import { getPresignedUrlExpiresAt, isPresignedS3Url, PdfFetchError } from "@client-shared/lib/pdfFetchUtils";
import { API_BATCH_SIZE, API_BATCH_SIZE_LARGE, API_BATCH_SIZE_THRESHOLD } from "@client-shared/lib/constants";
import type { CachedLabelUrlEntry } from "./cacheUtils";
import type { PurchaseOrder } from "@shared/types";

/**
 * Buffer applied before the presigned URL expiry — if a URL expires within
 * this window it is treated as expired to avoid mid-flight failures.
 */
export const PRESIGNED_URL_REFRESH_BUFFER_MS = 30_000;

/**
 * Returns true when a cached label URL entry is still fresh enough to use.
 * Entries without an expiry time (non-presigned URLs) are always considered fresh.
 */
export const isLabelUrlEntryFresh = (
  entry: CachedLabelUrlEntry | undefined,
  bufferMs: number = PRESIGNED_URL_REFRESH_BUFFER_MS
): boolean => {
  if (!entry?.url) {
    return false;
  }
  if (!entry.expiresAt) {
    return true;
  }
  return entry.expiresAt - Date.now() > bufferMs;
};

/**
 * Converts a raw URL map (purchaseOrderId → URL | null) into a map of
 * CachedLabelUrlEntry objects, including expiry information for presigned URLs.
 * Entries with null URLs are omitted.
 */
export const buildCachedLabelUrlMap = (urlMap: Record<string, string | null>): Record<string, CachedLabelUrlEntry> => {
  const out: Record<string, CachedLabelUrlEntry> = {};
  for (const [purchaseOrderId, url] of Object.entries(urlMap)) {
    if (!url) {
      continue;
    }
    out[purchaseOrderId] = {
      url,
      expiresAt: isPresignedS3Url(url) ? getPresignedUrlExpiresAt(url) : undefined,
    };
  }
  return out;
};

/**
 * Returns true when the given error is a presigned S3 URL error that can be
 * resolved by refreshing the URLs (expired / 403 / AccessDenied).
 */
export const shouldRefreshPresignedLabelUrlsForError = (err: unknown): boolean => {
  if (!(err instanceof PdfFetchError)) {
    return false;
  }
  if (!err.data.isPresignedS3Url) {
    return false;
  }
  if (err.data.status === 403) {
    return true;
  }
  const snippet = (err.data.bodySnippet ?? "").toLowerCase();
  return snippet.includes("request has expired") || snippet.includes("expired") || snippet.includes("accessdenied");
};

/**
 * Returns cached label URLs for the given orders if the cache is valid and all
 * entries are fresh, otherwise returns null.
 */
export const getCachedLabelUrls = (
  orders: PurchaseOrder[],
  cacheKey: string,
  cachedLabelUrls: Record<string, CachedLabelUrlEntry>,
  currentCacheKey: string,
  isEntryFresh: (entry: CachedLabelUrlEntry | undefined) => boolean
): string[] | null => {
  if (cacheKey !== currentCacheKey || currentCacheKey === "" || Object.keys(cachedLabelUrls).length === 0) {
    return null;
  }
  const urls: string[] = [];
  for (const order of orders) {
    const entry = cachedLabelUrls[order.purchaseOrderId];
    if (!entry) {
      continue;
    }
    if (!isEntryFresh(entry)) {
      return null;
    }
    urls.push(entry.url);
  }
  return urls.length > 0 ? urls : null;
};

/**
 * Scans the provided cached entries and calls clearFn if any entry is within
 * the expiry buffer window. Calling code should pass Object.values(cachedUrlsRef.current).
 */
export const maybeClearExpiredCachedLabelUrls = (
  cachedEntries: CachedLabelUrlEntry[],
  clearFn: () => void,
  bufferMs: number = PRESIGNED_URL_REFRESH_BUFFER_MS
): void => {
  if (cachedEntries.length === 0) {
    return;
  }
  const now = Date.now();
  for (const entry of cachedEntries) {
    if (!entry?.url || !entry.expiresAt) {
      continue;
    }
    if (entry.expiresAt - now <= bufferMs) {
      clearFn();
      return;
    }
  }
};

/**
 * Splits an array of items into batches for API calls.
 * Uses a smaller batch size (largeSize) for large datasets to reduce 502/timeout risk.
 */
export const buildOrderBatches = <T>(
  items: T[],
  threshold: number = API_BATCH_SIZE_THRESHOLD,
  largeSize: number = API_BATCH_SIZE_LARGE,
  defaultSize: number = API_BATCH_SIZE
): T[][] => {
  const batchSize = items.length > threshold ? largeSize : defaultSize;
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }
  return batches;
};
